import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { GetTraceabilityData } from "../../api/api";
import { EmptyView } from "../../components/EmptyView";
import { Loader } from "../../components/Loader";
import { useAppContext } from "../../contexts/AppContext";
import { isArrayNullOrEmpty } from "../../helper/helper";
import { FruitTraceability } from "../../models/traceability/FruitTraceability";
import { DialogStatement } from "../contracts/component/DialogStatement";
import { PurchaseExportPanel } from "./PurchaseExportPanel";
import { PurchaseMap } from "./PurchaseMap";
import { PurchaseKpiBand } from "./PurchaseKpiBand";
import "./PurchaseReport.scss";
import { ChevronDownIcon } from "./PurchaseReportIcons";
import { PurchaseReportRow } from "./PurchaseReportRow";
import { getTraceabilityData } from "./traceabilityTypes";

export type TraceContractListItem = {
    isSelected: boolean,
    contract: FruitTraceability
}

type PurchaseReportProps = {
    client: ApolloClient<NormalizedCacheObject>,
    onViewMoreClick: (contract: FruitTraceability) => void,
    contracts: FruitTraceability[],
    setContracts: React.Dispatch<React.SetStateAction<FruitTraceability[]>>,
    endDate: dayjs.Dayjs,
    startDate: dayjs.Dayjs,
    setEndDate: React.Dispatch<React.SetStateAction<dayjs.Dayjs>>,
    setStartDate: React.Dispatch<React.SetStateAction<dayjs.Dayjs>>,
    startDateOpen: boolean,
    setStartDateOpen: React.Dispatch<React.SetStateAction<boolean>>,
    endDateOpen: boolean,
    setEndDateOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const TAB_PERFORMANCE = 'performance'
const TAB_SUPPLIERS = 'suppliers'

/** Shared by the header and every row, so the columns cannot drift apart. */
const TABLE_COLUMNS = '52px minmax(88px, 0.9fr) minmax(140px, 1.5fr) minmax(148px, 1.6fr) minmax(104px, 1fr) minmax(64px, 0.75fr) minmax(58px, 0.7fr) 82px'
/** Expanded map: deforestation and price move into the map's contract strip,
    and traceability shrinks to a dot inside the id cell. */
const TABLE_COLUMNS_COMPACT = '78px 90px 70px minmax(70px, 1fr) 34px'

type SortField = 'contractId' | 'marketDay' | 'traceable_to' | 'sources' | 'quantity' | 'price' | 'deforestation'

export function PurchaseReportPage({ client,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startDateOpen,
    endDateOpen,
    setStartDateOpen,
    setEndDateOpen,
    onViewMoreClick,
    contracts,
    setContracts }: PurchaseReportProps) {

    const { millSelected } = useAppContext()
    const { t } = useTranslation()

    const [loading, setLoading] = useState(false)
    const [sortField, setSortField] = useState<SortField>('contractId');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
    const [highlightedFromMap, setHighlightedFromMap] = useState<number | null>(null);
    const [searchParams, setSearchParams] = useSearchParams()
    const [selectedTab, setSelectedTab] = useState<string>(TAB_PERFORMANCE)
    const [exportsOpen, setExportsOpen] = useState(false)
    const [statementIdSelected, setStatementIdSelected] = useState<number | undefined>(undefined)
    const [mapExpanded, setMapExpanded] = useState(false)
    const mapCaptureRef = useRef<(() => Promise<Blob | null>) | null>(null)

    function pushType(tab: string) {
        searchParams.set('tab', tab)
        setSearchParams(searchParams) // pushes new URL
    }

    useEffect(() => {
        const tab = searchParams.get('tab')
        setSelectedTab(tab ?? TAB_PERFORMANCE)
    }, [searchParams])

    const fetchContracts = () => {
        if (startDate && endDate && millSelected) {
            const marketDayStart = startDate.format('YYYY-MM-DD');
            const marketDayEnd = endDate.format('YYYY-MM-DD');
            setLoading(true);
            client.query({
                query: GetTraceabilityData,
                variables: {
                    filter: {
                        $and: [
                            {
                                market_day: {
                                    $gte: marketDayStart,
                                    $lte: marketDayEnd
                                }
                            },
                            {
                                mill_id: {
                                    $in: [millSelected.id]
                                }
                            }
                        ]
                    },
                    "pipeline": [
                        {
                            "$match": {
                                "market_day": {
                                    "$gte": marketDayStart,
                                    "$lte": marketDayEnd
                                },
                                "mill_id": {
                                    "$in": [millSelected.id]
                                }
                            }
                        },
                        {
                            "$group": {
                                "_id": "$mill_name",
                                "count": {
                                    "$sum": 1
                                }
                            }
                        }
                    ]
                },
                context: {
                    headers: {
                        "role": "usermill"
                    }
                }
            }).then((res) => {
                const contractsData = res?.data?.contracts?.map(c => FruitTraceability.build(c)) ?? [];
                setContracts(contractsData);
                setSelectedContractId(null);
            }).finally(() => {
                setLoading(false);
            });
        }
    }

    useEffect(() => {
        fetchContracts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, millSelected])

    const onStartDateSelected = (newValue) => {
        setStartDateOpen(false)
        setStartDate(newValue)
        if (!endDate) {
            setEndDateOpen(true)
        }
    }

    const onEndDateSelected = (newValue) => {
        setEndDateOpen(false)
        setEndDate(newValue)
        if (!startDate) {
            setStartDateOpen(true)
        }
    }

    const sortedContracts = contracts ? [...contracts].sort((a: FruitTraceability, b: FruitTraceability) => {
        let aValue, bValue;
        switch (sortField) {
            case 'contractId':
                aValue = a.contract_id;
                bValue = b.contract_id;
                break;
            case 'marketDay':
                aValue = a.market_day;
                bValue = b.market_day;
                break;
            case 'traceable_to':
                aValue = getTraceabilityData(a.traceabilityLabel()).label ?? '';
                bValue = getTraceabilityData(b.traceabilityLabel()).label ?? '';
                break;
            case 'sources':
                aValue = a.sourceCount();
                bValue = b.sourceCount();
                break;
            case 'quantity':
                aValue = a.quantity;
                bValue = b.quantity;
                break;
            case 'price':
                aValue = a.price;
                bValue = b.price;
                break;
            case 'deforestation':
                aValue = t(a.deforestationLabel());
                bValue = t(b.deforestationLabel());
                break;
            default:
                aValue = a.contract_id;
                bValue = b.contract_id;
        }
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    }) : [];

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortMarker = (field: SortField) => sortField === field
        ? (sortDirection === 'asc' ? ' ▲' : ' ▼')
        : ''

    function isSelected(tab: string) {
        return tab === selectedTab
    }

    // Map click: focus the contract, then surface and pulse its row.
    const handleMapSelect = (contractId: number) => {
        setSelectedContractId(contractId);
        setHighlightedFromMap(contractId);

        setTimeout(() => {
            document.querySelector(`[data-contract-id="${contractId}"]`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);

        setTimeout(() => setHighlightedFromMap(null), 3000);
    };

    const onTabClicked = (tab: string) => {
        pushType(tab)
    }

    const isEmpty = isArrayNullOrEmpty(sortedContracts)

    return (
        <div className="ds-page purchase-report">
            <h1 className="ds-page__title">{t('purchase_report')}</h1>
            <p className="ds-page__subtitle">{t('purchase_report_subtitle')}</p>

            <div className="ds-tabs">
                <button
                    key={TAB_PERFORMANCE}
                    type="button"
                    className={`ds-tab ${isSelected(TAB_PERFORMANCE) ? 'ds-tab--active' : ''}`}
                    onClick={() => onTabClicked(TAB_PERFORMANCE)}>
                    {t('contract_performance')}
                </button>
                <button
                    key={TAB_SUPPLIERS}
                    type="button"
                    className={`ds-tab ${isSelected(TAB_SUPPLIERS) ? 'ds-tab--active' : ''}`}
                    onClick={() => onTabClicked(TAB_SUPPLIERS)}>
                    {t('external_suppliers_traceability')}
                </button>
            </div>

            {/* Contract performance */}
            <div style={{ display: isSelected(TAB_PERFORMANCE) ? "block" : "none" }}>
                <PurchaseKpiBand
                    contracts={sortedContracts}
                    startDate={startDate}
                    endDate={endDate}
                    millCoordinates={millSelected?.position?.coordinates}
                />

                <div className="purchase-report__split" data-expanded={mapExpanded}>
                    <div className="purchase-report__main">
                        <div className="purchase-report__filters">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div className="ds-field">
                                    <span className="ds-field__label">{t('chose_start_date')}</span>
                                    <DatePicker
                                        maxDate={dayjs()}
                                        onClose={() => setStartDateOpen(false)}
                                        open={startDateOpen}
                                        onOpen={() => setStartDateOpen(!startDateOpen)}
                                        defaultValue={startDate}
                                        onChange={(newValue) => onStartDateSelected(newValue)}
                                    />
                                </div>
                                <div className="ds-field">
                                    <span className="ds-field__label">{t('chose_end_date')}</span>
                                    <DatePicker
                                        maxDate={endDate}
                                        onClose={() => setEndDateOpen(false)}
                                        open={endDateOpen}
                                        onOpen={() => setEndDateOpen(!endDateOpen)}
                                        defaultValue={endDate}
                                        onChange={(newValue) => onEndDateSelected(newValue)}
                                    />
                                </div>
                            </LocalizationProvider>
                            <button
                                type="button"
                                className="ds-btn ds-btn--primary purchase-report__dl-btn"
                                data-open={exportsOpen}
                                disabled={isEmpty}
                                onClick={() => setExportsOpen(!exportsOpen)}
                            >
                                {t('download_data')}
                                <ChevronDownIcon />
                            </button>
                        </div>

                        <div className="ds-collapse" data-open={exportsOpen && !isEmpty}>
                            <div className="ds-collapse__inner">
                                <PurchaseExportPanel
                                    millId={millSelected?.id}
                                    from={startDate.format('YYYY-MM-DD')}
                                    to={endDate.format('YYYY-MM-DD')}
                                    fileStamp={`${startDate.format('YYYY_MM_DD')}.${endDate.format('YYYY_MM_DD')}`}
                                    captureMap={() => mapCaptureRef.current?.() ?? Promise.resolve(null)}
                                    isDisable={isEmpty}
                                    hasTtpDataAccess={millSelected?.hasTtpDataAccess() ?? false}
                                />
                            </div>
                        </div>

                        {!isEmpty && (
                            <div
                                className="ds-grid-table"
                                style={{
                                    ['--ds-cols' as any]: mapExpanded ? TABLE_COLUMNS_COMPACT : TABLE_COLUMNS,
                                    ['--ds-min-width' as any]: mapExpanded ? '360px' : '760px',
                                }}
                            >
                                <div className="ds-grid-table__head">
                                    <span onClick={() => handleSort('contractId')}>ID{sortMarker('contractId')}</span>
                                    <span onClick={() => handleSort('marketDay')}>{t('delivery_date')}{sortMarker('marketDay')}</span>
                                    {!mapExpanded && (
                                        <>
                                            <span onClick={() => handleSort('traceable_to')} style={{ paddingLeft: 19 }}>{t('traceable_to')}{sortMarker('traceable_to')}</span>
                                            <span onClick={() => handleSort('deforestation')} style={{ paddingLeft: 9 }}>{t('deforestation')}{sortMarker('deforestation')}</span>
                                        </>
                                    )}
                                    <span onClick={() => handleSort('sources')} style={{ paddingLeft: mapExpanded ? 0 : 12, whiteSpace: 'nowrap' }}>{t('sources')}{sortMarker('sources')}</span>
                                    <span onClick={() => handleSort('quantity')} className="ds-grid-table__cell--num">{t('quantity')}{sortMarker('quantity')}</span>
                                    {!mapExpanded && (
                                        <span onClick={() => handleSort('price')} className="ds-grid-table__cell--num">{t('price')}{sortMarker('price')}</span>
                                    )}
                                    <span></span>
                                </div>
                                {sortedContracts?.map((contract, idx) => (
                                    <PurchaseReportRow
                                        key={contract.contract_id ? contract.contract_id : idx}
                                        contract={contract}
                                        compact={mapExpanded}
                                        isSelected={selectedContractId === contract.contract_id}
                                        onDetailClick={(contractClicked) => {
                                            if (contractClicked?.isNac()) {
                                                setStatementIdSelected(contractClicked.seller_statements?.at(0)?.statement_id)
                                            } else {
                                                onViewMoreClick(contractClicked)
                                            }
                                        }}
                                        onContractClick={(clicked) => setSelectedContractId(clicked.contract_id)}
                                        highlightedFromMap={highlightedFromMap === contract.contract_id}
                                    />
                                ))}
                            </div>
                        )}

                        <div className="pempem-flex-row-center" style={{ paddingTop: 10, marginTop: 10 }}>
                            <EmptyView isVisible={isEmpty} type="clock" title={t('no_contract_filtered')} />
                        </div>
                    </div>

                    <div className="purchase-report__map">
                        <PurchaseMap
                            contracts={sortedContracts}
                            mill={millSelected}
                            selectedContractId={selectedContractId}
                            onSelectContract={handleMapSelect}
                            onClearSelection={() => setSelectedContractId(null)}
                            expanded={mapExpanded}
                            onToggleExpanded={() => setMapExpanded(!mapExpanded)}
                            resize={selectedTab}
                            captureRef={mapCaptureRef}
                        />
                    </div>
                </div>
            </div>

            {/* External suppliers & traceability — specified in a later pass. */}
            <div style={{ display: isSelected(TAB_SUPPLIERS) ? "block" : "none" }}>
                <div className="pempem-flex-row-center" style={{ paddingTop: 40 }}>
                    <EmptyView isVisible={true} type="clock" title={t('coming_soon')} />
                </div>
            </div>

            <DialogStatement statementId={statementIdSelected} isOpen={statementIdSelected !== undefined} onClose={() => setStatementIdSelected(undefined)} />
            <Loader isLoading={loading} />
        </div>
    )
}
